import { ConnectHeader } from '@/pages/company/common/ConnectHeader';
import {
    Box,
    CircularProgress,
    Modal as ModalContainer,
    Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import {
    OutlinedSecondaryButtonLG,
    PrimaryButtonLG
} from '@/components/shared/Button';
import { SMBold, TEXT_SIZE } from '@/typography/Typography';

import ArrowBackIosNewOutlinedIcon from '@mui/icons-material/ArrowBackIosNewOutlined';
import ArrowForwardIosOutlinedIcon from '@mui/icons-material/ArrowForwardIosOutlined';
import { ConfirmationBox } from '@/pages/company/componants/Base';
import { useGetDocumentImage } from '@/services/paymentsQuery';
import { UploadDocResponse } from '@/services/PaymentsServiceV2';

interface MarkingDialogProps {
    open: boolean;
    onClose: () => void;
    markingDocument?: UploadDocResponse;
    handleMarkingDone?: (signatureData: {
        signatureX: number;
        signatureY: number;
        signatureWidth: number;
        signatureHeight: number;
        signaturePageNumber: number;
    }) => void;
}

interface Rectangle {
    x: number;
    y: number;
    width: number;
    height: number;
}

interface SelectionArea {
    startX: number;
    startY: number;
    endX: number;
    endY: number;
}

// page pixeel size to be supplied for backend

export const MarkingDialog = ({
    open,
    onClose,
    handleMarkingDone,
    markingDocument
}: MarkingDialogProps) => {
    const { t } = useTranslation();

    const [page, setPage] = useState<number>(0);

    const { data, isPending } = useGetDocumentImage(
        markingDocument?.pdfUrl || '',
        page + 1,
        open
    );

    // console.log('Document Image Data:', markingDocument);

    const [isDrawing, setIsDrawing] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(
        null
    );
    const [selection, setSelection] = useState<SelectionArea | null>(null);
    const [selectedRectangle, setSelectedRectangle] =
        useState<Rectangle | null>(null);
    const imageRef = useRef<HTMLImageElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // Calculate scaled dimensions (70% of screen)
    const getScaledDimensions = useCallback(() => {
        const screenWidth = window.innerWidth;
        const screenHeight = window.innerHeight;
        const availableWidth = screenWidth * 0.7;
        const availableHeight = screenHeight * 0.7;

        const originalWidth = markingDocument?.pageWidth || 720;
        const originalHeight = markingDocument?.pageHeight || 540;

        // Calculate scale to fit within 70% of screen while maintaining aspect ratio
        const scaleX = availableWidth / originalWidth;
        const scaleY = availableHeight / originalHeight;
        const scale = Math.min(scaleX, scaleY);

        return {
            displayWidth: originalWidth * scale,
            displayHeight: originalHeight * scale,
            scale,
            originalWidth,
            originalHeight
        };
    }, [markingDocument?.pageWidth, markingDocument?.pageHeight]);

    const scaledDimensions = getScaledDimensions();

    // Helper function to check if a point is within the rectangle
    const isPointInRectangle = useCallback(
        (x: number, y: number): boolean => {
            if (
                !selectedRectangle ||
                !imageRef.current ||
                !containerRef.current
            )
                return false;

            // Convert rectangle coordinates (in original document space) to display coordinates
            const displayX = selectedRectangle.x * scaledDimensions.scale;
            const displayY = selectedRectangle.y * scaledDimensions.scale;
            const displayWidth =
                selectedRectangle.width * scaledDimensions.scale;
            const displayHeight =
                selectedRectangle.height * scaledDimensions.scale;

            return (
                x >= displayX &&
                x <= displayX + displayWidth &&
                y >= displayY &&
                y <= displayY + displayHeight
            );
        },
        [selectedRectangle, scaledDimensions]
    );

    // Helper function to get cursor style based on position
    const getCursorStyle = useCallback(
        (x: number, y: number): string => {
            if (isDragging) return 'grabbing';
            if (isDrawing) return 'crosshair';
            if (selectedRectangle && isPointInRectangle(x, y)) return 'grab';
            return 'crosshair';
        },
        [isDragging, isDrawing, selectedRectangle, isPointInRectangle]
    );

    const handleMouseDown = useCallback(
        (e: React.MouseEvent) => {
            if (!imageRef.current || !containerRef.current) return;

            const rect = containerRef.current.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;

            // Check if clicking within existing rectangle for dragging
            if (selectedRectangle && isPointInRectangle(x, y)) {
                setIsDragging(true);
                setDragStart({ x, y });
            } else {
                // Start new rectangle selection
                setIsDrawing(true);
                setSelection({
                    startX: x,
                    startY: y,
                    endX: x,
                    endY: y
                });
                setSelectedRectangle(null);
            }
        },
        [selectedRectangle, isPointInRectangle]
    );

    const handleMouseMove = useCallback(
        (e: React.MouseEvent) => {
            if (!containerRef.current) return;

            const rect = containerRef.current.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;

            if (isDrawing && selection) {
                // Handle rectangle drawing
                setSelection((prev) =>
                    prev
                        ? {
                              ...prev,
                              endX: x,
                              endY: y
                          }
                        : null
                );
            } else if (
                isDragging &&
                selectedRectangle &&
                dragStart &&
                containerRef.current
            ) {
                // Handle rectangle dragging
                const deltaX = x - dragStart.x;
                const deltaY = y - dragStart.y;

                // Convert delta from display coordinates to original document coordinates
                const actualDeltaX = deltaX / scaledDimensions.scale;
                const actualDeltaY = deltaY / scaledDimensions.scale;

                // Calculate new position in original document coordinates
                // Ensure it stays within original document bounds
                const newX = Math.max(
                    0,
                    Math.min(
                        selectedRectangle.x + actualDeltaX,
                        scaledDimensions.originalWidth - selectedRectangle.width
                    )
                );
                const newY = Math.max(
                    0,
                    Math.min(
                        selectedRectangle.y + actualDeltaY,
                        scaledDimensions.originalHeight -
                            selectedRectangle.height
                    )
                );

                setSelectedRectangle((prev) =>
                    prev
                        ? {
                              ...prev,
                              x: newX,
                              y: newY
                          }
                        : null
                );

                setDragStart({ x, y });
            }
        },
        [
            isDrawing,
            isDragging,
            selection,
            selectedRectangle,
            dragStart,
            scaledDimensions
        ]
    );

    const handleMouseUp = useCallback(() => {
        if (isDragging) {
            // Complete drag operation
            setIsDragging(false);
            setDragStart(null);
        } else if (
            isDrawing &&
            selection &&
            imageRef.current &&
            containerRef.current
        ) {
            // Complete drawing operation
            // Get selection coordinates relative to container (display coordinates)
            const displayX = Math.min(selection.startX, selection.endX);
            const displayY = Math.min(selection.startY, selection.endY);
            const displayWidth = Math.abs(selection.endX - selection.startX);
            const displayHeight = Math.abs(selection.endY - selection.startY);

            // Convert display coordinates to original document coordinates
            const actualX = displayX / scaledDimensions.scale;
            const actualY = displayY / scaledDimensions.scale;
            const actualWidth = displayWidth / scaledDimensions.scale;
            const actualHeight = displayHeight / scaledDimensions.scale;

            // Ensure coordinates stay within original document bounds
            const boundedX = Math.max(
                0,
                Math.min(actualX, scaledDimensions.originalWidth)
            );
            const boundedY = Math.max(
                0,
                Math.min(actualY, scaledDimensions.originalHeight)
            );
            const boundedWidth = Math.min(
                actualWidth,
                scaledDimensions.originalWidth - boundedX
            );
            const boundedHeight = Math.min(
                actualHeight,
                scaledDimensions.originalHeight - boundedY
            );

            if (displayWidth > 5 && displayHeight > 5) {
                // Only create rectangle if it's meaningful size
                const rectangle: Rectangle = {
                    x: Math.round(boundedX),
                    y: Math.round(boundedY),
                    width: Math.round(boundedWidth),
                    height: Math.round(boundedHeight)
                };

                setSelectedRectangle(rectangle);
            }

            setIsDrawing(false);
            setSelection(null);
        }
    }, [isDragging, isDrawing, selection, scaledDimensions]);

    const getSelectionStyle = useCallback(() => {
        if (!selection) return {};

        const x = Math.min(selection.startX, selection.endX);
        const y = Math.min(selection.startY, selection.endY);
        const width = Math.abs(selection.endX - selection.startX);
        const height = Math.abs(selection.endY - selection.startY);

        return {
            position: 'absolute' as const,
            left: x,
            top: y,
            width,
            height,
            border: '2px dashed #28a745',
            backgroundColor: 'rgba(40, 167, 69, 0.1)',
            pointerEvents: 'none' as const,
            zIndex: 10
        };
    }, [selection]);

    const imageId = useId();

    useEffect(() => {
        if (data?.data) {
            if (imageRef.current) {
                const imageUrl = URL.createObjectURL(data.data);
                imageRef.current.src = imageUrl;
            }
        }
    }, [data]);

    return (
        <ModalContainer open={open} onClose={onClose}>
            <ConfirmationBox
                style={{
                    width: 'auto',
                    maxWidth: '90vw',
                    maxHeight: '90vh',
                    overflow: 'auto'
                }}
            >
                <ConnectHeader title={t('documents.define_signature_area')} />

                <Typography
                    textAlign={'center'}
                    fontWeight={600}
                    fontSize={TEXT_SIZE.MEDIUM_SMALL}
                >
                    {t('Page')} {page + 1} / {markingDocument?.pageCount}
                </Typography>

                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        marginBottom: 3,
                        marginTop: 2,
                        marginLeft: 2
                    }}
                >
                    <ArrowBackIosNewOutlinedIcon
                        style={{ marginRight: 16 }}
                        color="primary"
                        onClick={() => {
                            setPage((prev) => Math.max(prev - 1, 0));
                        }}
                    />
                    <Box
                        ref={containerRef}
                        sx={{
                            boxShadow:
                                '0 4px 16px rgba(0,0,0,0.15), 0 1.5px 4px rgba(0,0,0,0.10)',
                            borderRadius: '4px',
                            background: '#fff',
                            overflow: 'hidden',
                            width: scaledDimensions.displayWidth,
                            height: scaledDimensions.displayHeight,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            position: 'relative',
                            userSelect: 'none'
                        }}
                        onMouseDown={handleMouseDown}
                        onMouseUp={handleMouseUp}
                        onMouseMove={(e) => {
                            if (!containerRef.current) return;
                            const rect =
                                containerRef.current.getBoundingClientRect();
                            const x = e.clientX - rect.left;
                            const y = e.clientY - rect.top;

                            // Update cursor based on position
                            if (containerRef.current) {
                                containerRef.current.style.cursor =
                                    getCursorStyle(x, y);
                            }

                            handleMouseMove(e);
                        }}
                        onMouseLeave={() => {
                            if (isDrawing) {
                                setIsDrawing(false);
                                setSelection(null);
                            }
                            if (isDragging) {
                                setIsDragging(false);
                                setDragStart(null);
                            }
                        }}
                    >
                        {!isPending && (
                            <img
                                id={imageId}
                                ref={imageRef}
                                // alt={`Page ${pages[page]?.number}`}
                                style={{
                                    width: scaledDimensions.displayWidth,
                                    height: scaledDimensions.displayHeight,
                                    objectFit: 'contain',
                                    background: '#f8f8f8',
                                    pointerEvents: 'none'
                                }}
                                draggable={false}
                            />
                        )}
                        {isPending && <CircularProgress />}

                        {selection && <div style={getSelectionStyle()} />}
                        {selectedRectangle && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    left:
                                        selectedRectangle.x *
                                        scaledDimensions.scale,
                                    top:
                                        selectedRectangle.y *
                                        scaledDimensions.scale,
                                    width:
                                        selectedRectangle.width *
                                        scaledDimensions.scale,
                                    height:
                                        selectedRectangle.height *
                                        scaledDimensions.scale,
                                    border: '2px solid #007bff',
                                    backgroundColor: 'rgba(0, 123, 255, 0.1)',
                                    pointerEvents: 'all',
                                    zIndex: 20,
                                    cursor: 'grab',
                                    '&:active': {
                                        cursor: 'grabbing'
                                    }
                                }}
                            />
                        )}
                    </Box>
                    <ArrowForwardIosOutlinedIcon
                        color="primary"
                        style={{ marginLeft: 16 }}
                        onClick={() => {
                            setPage((prev) =>
                                Math.min(
                                    prev + 1,
                                    (markingDocument?.pageCount ?? 1) - 1
                                )
                            );
                        }}
                    />
                </Box>

                {/* {selectedRectangle && (
                    <Box
                        sx={{
                            marginBottom: 2,
                            padding: 2,
                            backgroundColor: '#f8f9fa',
                            borderRadius: '4px',
                            border: '1px solid #e9ecef'
                        }}
                    >
                        <Typography
                            variant="body2"
                            fontWeight={600}
                            marginBottom={1}
                        >
                            Selected Area:
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {`X: ${selectedRectangle.x}px, Y: ${selectedRectangle.y}px`}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Width: {selectedRectangle.width}px, Height:{' '}
                            {selectedRectangle.height}px
                        </Typography>
                    </Box>
                )} */}

                <Box
                    width={'100%'}
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        mt: 4,
                        mb: 2
                    }}
                    gap={1.5}
                >
                    <OutlinedSecondaryButtonLG
                        sx={{ width: '50%' }}
                        onClick={() => {
                            setIsDrawing(false);
                            setSelection(null);
                            setSelectedRectangle(null);
                            onClose();
                        }}
                    >
                        <SMBold textTransform={'none'}>{t('cancel')}</SMBold>
                    </OutlinedSecondaryButtonLG>

                    <PrimaryButtonLG
                        onClick={() => {
                            if (selectedRectangle) {
                                handleMarkingDone?.({
                                    signatureX: selectedRectangle.x,
                                    signatureY: selectedRectangle.y,
                                    signatureWidth: selectedRectangle.width,
                                    signatureHeight: selectedRectangle.height,
                                    signaturePageNumber: page + 1
                                });
                            }
                            onClose();
                        }}
                        sx={{ width: '50%' }}
                        disabled={!selectedRectangle}
                    >
                        <SMBold textTransform={'none'}>
                            {t('documents.save_area')}
                        </SMBold>
                    </PrimaryButtonLG>
                </Box>
            </ConfirmationBox>
        </ModalContainer>
    );
};
